import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Input, message } from 'antd'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { COLORS } from 'theme'

import TreeSelect from 'components/TreeSelect'
import Pagination from 'components/Pagination'
import { StyledTableSqare } from 'components/TableStyles/TableStylesSqare/TableStylesSqare'
import FilterChipComponent from 'components/UIComponents/FilterChipComponent'
import DownloadButton from 'components/DownloadButton'
// import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts' // Removed custom charts

import {
    fetchDiagnosisData,
    setChartFilters as setChartFiltersAction,
    clearChartFilters as clearChartFiltersAction,
    getAssessmentDomains,
    getAssessmentDegrees,
    getAssessmentDepartments,
    getAssessmentSpecializations,
    exportDiagnosisDataToExcel
} from '../../actions'
import AssessmentCharts from '../../components/AssessmentCharts'
import AssessmentNavBar from '../../components/AssessmentNavBar'
import StudentReportModal from '../StudentReport'

import {
    SearchSection,
    ShowingText,
    TableTop,
} from '../../Style/style'

import {
    DiagnosisListWrapper,
    FilterInlineContainer,
    FilterGrid,
    FilterGroup,
    FilterLabel,
    StyledFormItem,
    HeaderFullWidth,
    FilterSection,
    CandidateInfo,
    CandidateName,
    CandidateId,
    ContactInfo,
    LevelBadge,
    ScoreBar,
    ScoreBarTrack,
    ScoreBarFill,
    ScoreValue,
    StatsRow,
    StatCard,
    StatLabel,
    StatValue,
    NoData,
} from './styles'

// Styled components matching CandidateList
const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;
  
  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }
  
  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;
    
    &:focus {
      box-shadow: none !important;
    }
  }
`

const AlignedTableTop = styled(TableTop)`
  margin-bottom: 0;
  
  ${SearchSection} {
    margin-bottom: 0;
  }
  
  ${ShowingText} {
    margin-bottom: 0;
  }
`

// Helper to structure data for TreeSelect
// Filters out invalid entries (empty, dashes, etc.)
const buildTreeData = (data, selectAllLabel = 'Select All') => {
    if (!data || data.length === 0) return []

    // Filter out invalid entries (empty names, dashes, etc.)
    const validData = data.filter(item => {
        const name = item?.name || item?.label || ''
        const trimmedName = String(name).trim()
        return trimmedName && trimmedName !== '-' && trimmedName !== '---' && trimmedName.toLowerCase() !== 'null'
    })

    if (validData.length === 0) {
        return []
    }

    const children = validData.map(item => ({
        title: item.name || item.label,
        value: item.id || item.value,
        key: item.id || item.value,
    }))
    return [{
        title: selectAllLabel,
        value: 'all',
        key: 'all',
        children: children,
    }]
}

const DiagnosisList = ({
    scheduleId,
    passingYear,
    instituteId,
    latestAssessmentInstituteMapId,
    assessmentType,
    // Schedule info for header display
    scheduleName,
    // Navigation props
    assessmentDetails = [],
    onSwitchToAssessment,
    // Filter props from parent (cascading)
    topLevelDomain = [],
    setTopLevelDomain,
    topLevelDegree = [],
    setTopLevelDegree,
    topLevelDepartment = [],
    setTopLevelDepartment,
    topLevelSpecialization = [],
    setTopLevelSpecialization,
}) => {
    const dispatch = useDispatch()

    // Redux filters
    const chartFiltersFromRedux = useSelector(state => state.SubscribedInstitutes?.chartFilters)

    // Local state for diagnosis data
    const [localDiagnosisData, setLocalDiagnosisData] = useState({
        students: [],
        totalCount: 0,
        completedDiagnosisCount: 0,
        pageNumber: 1,
        pageSize: 10,
        assessmentType: assessmentType || null,
        distributions: null // New field from API
    })
    const [isLoading, setIsLoading] = useState(false)

    // Filter state
    const [searchTerm, setSearchTerm] = useState('')
    const [localSearchTerm, setLocalSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize] = useState(10)
    const [sortBy, setSortBy] = useState({ field: 'name', order: 'asc' })

    // State for StudentReportDrawer
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [selectedStudent, setSelectedStudent] = useState(null)

    // Filter options (Fetched locally for cascading)
    const [domainOptions, setDomainOptions] = useState([])
    const [degreeOptions, setDegreeOptions] = useState([])
    const [departmentOptions, setDepartmentOptions] = useState([])
    const [specializationOptions, setSpecializationOptions] = useState([])

    const debounceRef = useRef(null)

    // Helper to extract IDs
    const extractIds = (items) => items?.map(i => i.value || i) || []

    // 1. Fetch Domains on Mount or scheduleId change (for diagnosis)
    // If domains is empty, auto-fetch degrees
    useEffect(() => {
        const fetchDomains = async () => {
            if (scheduleId || instituteId) {
                const results = await dispatch(getAssessmentDomains({
                    scheduleId: scheduleId,
                    passingYear: passingYear || '',
                    instituteId: instituteId || '',
                }))
                if (results && results.length > 0) {
                    setDomainOptions(buildTreeData(results, 'Select All Domains'))
                } else {
                    // Domains is empty, auto-fetch degrees
                    setDomainOptions([])
                    const degreeResults = await dispatch(getAssessmentDegrees({
                        scheduleId: scheduleId,
                        passingYear: passingYear || '',
                        domains: [] // Empty array to fetch all
                    }))
                    if (degreeResults && degreeResults.length > 0) {
                        setDegreeOptions(buildTreeData(degreeResults, 'Select All Degrees'))
                    } else {
                        // Degrees is also empty, auto-fetch departments
                        setDegreeOptions([])
                        const deptResults = await dispatch(getAssessmentDepartments({
                            scheduleId: scheduleId,
                            passingYear: passingYear || '',
                            degrees: [] // Empty array to fetch all
                        }))
                        if (deptResults && deptResults.length > 0) {
                            setDepartmentOptions(buildTreeData(deptResults, 'Select All Depts.'))
                        } else {
                            // Departments is also empty, auto-fetch specializations
                            setDepartmentOptions([])
                            const specResults = await dispatch(getAssessmentSpecializations({
                                scheduleId: scheduleId,
                                passingYear: passingYear || '',
                                departments: [] // Empty array to fetch all
                            }))
                            if (specResults) {
                                setSpecializationOptions(buildTreeData(specResults, 'Select All Specs.'))
                            }
                        }
                    }
                }
            }
        }
        fetchDomains()
    }, [dispatch, scheduleId, passingYear, instituteId])

    // 2. Fetch Degrees (Independent of Domain)
    useEffect(() => {
        const fetchDegrees = async () => {
            if (scheduleId) {
                const results = await dispatch(getAssessmentDegrees({
                    scheduleId: scheduleId,
                    passingYear: passingYear || '',
                    domains: [] // Fetch all degrees
                }))
                if (results && results.length > 0) {
                    setDegreeOptions(buildTreeData(results, 'Select All Degrees'))
                } else {
                    // Degrees is empty, auto-fetch departments
                    setDegreeOptions([])
                    const deptResults = await dispatch(getAssessmentDepartments({
                        scheduleId: scheduleId,
                        passingYear: passingYear || '',
                        degrees: []
                    }))
                    if (deptResults && deptResults.length > 0) {
                        setDepartmentOptions(buildTreeData(deptResults, 'Select All Depts.'))
                    } else {
                        // Departments is also empty, auto-fetch specializations
                        setDepartmentOptions([])
                        const specResults = await dispatch(getAssessmentSpecializations({
                            scheduleId: scheduleId,
                            passingYear: passingYear || '',
                            departments: []
                        }))
                        if (specResults) {
                            setSpecializationOptions(buildTreeData(specResults, 'Select All Specs.'))
                        }
                    }
                }
            } else {
                setDegreeOptions([])
            }
        }
        fetchDegrees()
    }, [dispatch, scheduleId])

    // 3. Fetch Departments when Degree changes (Strict Cascading)
    // If departments is empty, auto-fetch specializations
    useEffect(() => {
        const fetchDepartments = async () => {
            const degreeIds = extractIds(topLevelDegree).filter(id => id !== 'all')

            // Only fetch if Degree is selected
            if (scheduleId && degreeIds.length > 0) {
                const results = await dispatch(getAssessmentDepartments({
                    scheduleId: scheduleId,
                    passingYear: passingYear || '',
                    degrees: degreeIds
                }))
                if (results && results.length > 0) {
                    setDepartmentOptions(buildTreeData(results, 'Select All Depts.'))
                } else {
                    // Departments is empty, auto-fetch specializations
                    setDepartmentOptions([])
                    const specResults = await dispatch(getAssessmentSpecializations({
                        scheduleId: scheduleId,
                        passingYear: passingYear || '',
                        departments: [] // Empty array to fetch all
                    }))
                    if (specResults) {
                        setSpecializationOptions(buildTreeData(specResults, 'Select All Specs.'))
                    }
                }
            } else {
                setDepartmentOptions([])
            }
        }
        fetchDepartments()
    }, [dispatch, scheduleId, topLevelDegree])

    // 4. Fetch Specializations when Department changes (Strict Cascading)
    useEffect(() => {
        const fetchSpecializations = async () => {
            const departmentIds = extractIds(topLevelDepartment).filter(id => id !== 'all')

            // Only fetch if Department is selected
            if (scheduleId && departmentIds.length > 0) {
                const results = await dispatch(getAssessmentSpecializations({
                    scheduleId: scheduleId,
                    passingYear: passingYear || '',
                    departments: departmentIds
                }))
                if (results) setSpecializationOptions(buildTreeData(results, 'Select All Specs.'))
            } else {
                setSpecializationOptions([])
            }
        }
        fetchSpecializations()
    }, [dispatch, scheduleId, topLevelDepartment])

    // Fetch diagnosis data
    const fetchData = useCallback(async () => {
        if (!scheduleId) return

        setIsLoading(true)
        try {
            const domainIds = topLevelDomain?.map(d => d.value || d) || []
            const degreeIds = topLevelDegree?.map(d => d.value || d) || []
            const departmentIds = topLevelDepartment?.map(d => d.value || d) || []
            const specializationIds = topLevelSpecialization?.map(s => s.value || s) || []

            // Extract chart filters map params
            const cefrLevel = chartFiltersFromRedux?.cefrLevel || null
            const aptitudeLevel = chartFiltersFromRedux?.aptitudeLevel || null
            const consistency = chartFiltersFromRedux?.consistency || null

            const result = await dispatch(fetchDiagnosisData({
                scheduleId,
                passingYear: passingYear || '',
                searchTerm,
                domain: domainIds.filter(id => id !== 'all'),
                degree: degreeIds.filter(id => id !== 'all'),
                department: departmentIds.filter(id => id !== 'all'),
                specialization: specializationIds.filter(id => id !== 'all'),
                pageNumber: currentPage,
                pageSize,
                sortBy,
                // Chart filters
                cefrLevel,
                aptitudeLevel,
                consistency
            }))
            if (result) {
                setLocalDiagnosisData(result)
            }
        } catch (error) {
            console.error('[DiagnosisList] Fetch error:', error)
        } finally {
            setIsLoading(false)
        }
    }, [dispatch, scheduleId, passingYear, searchTerm, topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization, currentPage, pageSize, sortBy, chartFiltersFromRedux])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    // Debounced search handler
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value
        setLocalSearchTerm(value)

        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setSearchTerm(value)
            setCurrentPage(1)
        }, 500)
    }, [])

    // Export handler
    const handleExport = useCallback(async () => {
        try {
            message.loading({ content: 'Preparing export...', key: 'export' })

            const domainIds = topLevelDomain?.map(d => d.value || d) || []
            const degreeIds = topLevelDegree?.map(d => d.value || d) || []
            const departmentIds = topLevelDepartment?.map(d => d.value || d) || []
            const specializationIds = topLevelSpecialization?.map(s => s.value || s) || []

            const { cefrLevel, aptitudeLevel, consistency } = chartFiltersFromRedux || {}

            await dispatch(exportDiagnosisDataToExcel({
                scheduleId,
                searchTerm,
                passingYear: passingYear || '',
                domain: domainIds.filter(id => id !== 'all'),
                degree: degreeIds.filter(id => id !== 'all'),
                department: departmentIds.filter(id => id !== 'all'),
                specialization: specializationIds.filter(id => id !== 'all'),
                Cefr: cefrLevel ? [cefrLevel] : [],
                AptitudeLevel: aptitudeLevel ? [aptitudeLevel] : [],
                consistency: consistency ? [consistency] : [],
                sortBy
            }))

            message.success({ content: 'Export completed!', key: 'export' })
        } catch (error) {
            console.error('Export failed:', error)
            message.error({ content: 'Export failed. Please try again.', key: 'export' })
        }
    }, [dispatch, scheduleId, searchTerm, passingYear, topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization, chartFiltersFromRedux, sortBy])

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    // Handlers for charts
    const handleSetChartFilters = (filters) => {
        dispatch(setChartFiltersAction(filters))
        setCurrentPage(1) // Reset page on filter
    }

    const handleClearChartFilters = () => {
        dispatch(clearChartFiltersAction())
        setCurrentPage(1)
    }

    // Dynamic Section Columns
    const sectionColumns = useMemo(() => {
        const type = localDiagnosisData.assessmentType?.toLowerCase()
        if (type === 'communication' || type === 'hinglish') {
            const sections = ['Reading', 'Listening', 'Speaking', 'Writing']
            return sections.map(section => ({
                title: section,
                dataIndex: section.toLowerCase(),
                key: section.toLowerCase(),
                width: 100,
                align: 'center',
                sorter: true,
                render: (_, record) => {
                    const val = record.sectionScores?.[section.toLowerCase()]
                    // val is { average: 50, ... }
                    return (val?.average !== undefined && val?.average !== null) ? `${val.average}` : '-'
                }
            }))
        } else if (type === 'aptitude') {
            // Use proper field names that match backend sort keys
            const sectionMap = [
                { title: 'Critical', field: 'critical' },
                { title: 'Quantitative', field: 'quantitative' },
                { title: 'Logical', field: 'logical' }
            ]
            return sectionMap.map(({ title, field }) => ({
                title: title,
                dataIndex: field,
                key: field,
                width: 100,
                align: 'center',
                sorter: true,
                render: (_, record) => {
                    const val = record.sectionScores?.[field]
                    return (val?.average !== undefined && val?.average !== null) ? `${val.average}` : '-'
                }
            }))
        }
        return []
    }, [localDiagnosisData.assessmentType])

    // Construct mock assessment object for charts
    const mockAssessment = useMemo(() => {
        if (!scheduleId) return null
        return {
            id: scheduleId,
            assessmentType: localDiagnosisData.assessmentType || '', // Empty initially
            assessmentName: 'Diagnosis Assessment',
            scheduleId: scheduleId
        }
    }, [localDiagnosisData.assessmentType, latestAssessmentInstituteMapId, scheduleId])

    // Table sorting handler
    const onSorting = (pagination, filters, sorter) => {
        if (sorter.order) {
            setSortBy({
                field: sorter.field,
                order: sorter.order === 'ascend' ? 'asc' : 'desc'
            })
        } else {
            setSortBy({ field: 'name', order: 'asc' })
        }
    }

    // --- Filter Chips Logic (Matching CandidateList) ---
    const [isChipModalVisible, setIsChipModalVisible] = useState(false)
    const handleChipModalToggle = () => setIsChipModalVisible(prev => !prev)

    const filterArr = useMemo(() => {
        const chips = []

        topLevelDomain.forEach(item =>
            chips.push({ label: item.title || item.label, value: item.title || item.label, id: `domain_${item.value}` })
        )

        topLevelDegree.forEach(item =>
            chips.push({ label: item.title || item.label, value: item.title || item.label, id: `degree_${item.value}` })
        )

        topLevelDepartment.forEach(item =>
            chips.push({ label: item.title || item.label, value: item.title || item.label, id: `department_${item.value}` })
        )

        topLevelSpecialization.forEach(item =>
            chips.push({ label: item.title || item.label, value: item.title || item.label, id: `specialization_${item.value}` })
        )

        return chips
    }, [topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization])

    const handleRemoveFilterChip = chip => {
        const [type, ...valueParts] = chip.id.split('_')
        const value = valueParts.join('_')

        switch (type) {
            case 'domain':
                setTopLevelDomain(topLevelDomain.filter(item => item.value !== value))
                break
            case 'degree':
                setTopLevelDegree(topLevelDegree.filter(item => item.value !== value))
                break
            case 'department':
                setTopLevelDepartment(topLevelDepartment.filter(item => item.value !== value))
                break
            case 'specialization':
                setTopLevelSpecialization(topLevelSpecialization.filter(item => item.value !== value))
                break
            default:
                break
        }
    }

    // Table columns matching CandidateList style
    const columns = [
        {
            title: 'Candidate',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            width: 250,
            fixed: 'left',
            render: (text, record) => {
                let phone = record.phoneNumber
                const p = String(phone || '').trim()
                if (!p || /^(?:\+?91)?[\s-]*$/.test(p)) {
                    phone = '-'
                }

                return (
                    <CandidateInfo
                        onClick={() => {
                            setSelectedStudent(record)
                            setDrawerVisible(true)
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <div>
                            <CandidateName>{record.name || '-'}</CandidateName>
                            <CandidateId style={{ marginBottom: 4 }}>{record.email || '-'}</CandidateId>
                            <ContactInfo>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>{phone}</span>
                            </ContactInfo>
                        </div>
                    </CandidateInfo>
                )
            },
        },
        {
            title: 'Degree & Department',
            key: 'degreeDept',
            width: 200,
            render: (_, record) => (
                <ContactInfo>
                    <span>{record.degree || '-'}</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{record.department || '-'}</span>
                </ContactInfo>
            ),
        },
        {
            title: 'Diagnosis Status',
            dataIndex: 'takenSent',
            key: 'takenSent',
            width: 140,
            align: 'center',
            render: val => (
                <span style={{
                    color: val === '2/2' ? '#16a34a' : '#f59e0b',
                    fontWeight: 600
                }}>
                    {val || '-'}
                </span>
            ),
        },
        {
            title: 'Assigned Difficulty',
            dataIndex: 'diagnosisLevel',
            key: 'diagnosisLevel',
            width: 140,
            align: 'center',
            render: val => {
                if (!val) return '-'

                // Capitalize (e.g. "medium" -> "Medium")
                const display = val.charAt(0).toUpperCase() + val.slice(1);

                return <LevelBadge level={display}>{display}</LevelBadge>
            },
        },
        {
            title: 'Progression Level',
            dataIndex: 'achievedLevelDisplay',
            key: 'achievedLevel',
            sorter: true,
            width: 140,
            align: 'center',
            // Use achievedLevelDisplay for friendly names (e.g., "A1-Beginner"), fallback to achievedLevel
            render: (val, record) => {
                const displayVal = val || record.achievedLevel
                return displayVal ? <LevelBadge level={displayVal}>{displayVal}</LevelBadge> : '-'
            },
        },
        {
            title: 'SCORE',
            dataIndex: 'npsScore',
            key: 'npsScore',
            sorter: true,
            width: 150,
            align: 'center',
            render: (val) => {
                const score = val !== null && val !== undefined ? val : null
                return score !== null ? (
                    <ScoreBar>
                        {/* <ScoreBarTrack>
                        <ScoreBarFill percentage={score} />
                    </ScoreBarTrack> */}
                        <ScoreValue>{score.toFixed(2)}</ScoreValue>
                    </ScoreBar>
                ) : '-'
            },
        },
        // Insert Dynamic Section Columns here
        ...sectionColumns,
        // Proctoring column at the end
        {
            title: 'Proctoring',
            dataIndex: 'proctoring',
            key: 'proctoring',
            width: 100,
            align: 'center',
            render: (val) => {
                if (!val || val === '-') return '-'
                const isBad = val.toLowerCase() === 'bad' || val.toLowerCase() === 'poor'
                return (
                    <span style={{
                        color: isBad ? '#ef4444' : '#16a34a',
                        fontWeight: 600
                    }}>
                        {val}
                    </span>
                )
            }
        },
    ]

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    // Filter students array based on chart filters?
    // No, backend already filters 'localDiagnosisData.students' based on passed params.
    // So we just render 'localDiagnosisData.students'.

    // Helper to format assessment type for display
    const getAssessmentTypeDisplay = (type) => {
        const typeMap = {
            communication: 'Communication Assessment',
            hinglish: 'Communication Assessment',
            behavior: 'Behavioral Assessment',
            aptitude: 'Aptitude',
            role_based: 'Role Based Assessment',
            technical_mcq: 'Technical MCQ',
            technical_coding: 'Technical Coding',
        }
        const normalizedType = (type || '').toLowerCase().replace(/[\s-]+/g, '_')
        return typeMap[normalizedType] || typeMap[type] || type || ''
    }

    return (
        <DiagnosisListWrapper>
            {/* Schedule Name Header */}
            {scheduleName && (
                <div style={{
                    padding: '12px 0',
                    marginBottom: '8px',
                    borderBottom: '1px solid #e8e8e8',
                }}>
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#1f2937',
                    }}>
                        {scheduleName}-diagnosis
                    </span>
                    {assessmentType && (
                        <span style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginLeft: '8px',
                        }}>
                            ({getAssessmentTypeDisplay(assessmentType)})
                        </span>
                    )}
                </div>
            )}
            {/* Assessment Navigation */}
            {assessmentDetails.length > 0 && onSwitchToAssessment && (() => {
                const normalizedType = (assessmentType || '').toLowerCase()
                const hasDiagnosis = ['communication', 'aptitude', 'hinglish'].includes(normalizedType)
                const diagEntry = { isDiagnosis: true, title: 'Diagnosis', status: 'Completed' }
                const navItems = hasDiagnosis ? [diagEntry, ...assessmentDetails] : assessmentDetails
                return navItems.length > 1 ? (
                    <AssessmentNavBar
                        assessmentList={navItems}
                        currentIndex={0}
                        onNavigate={(idx) => {
                            if (idx > 0 || !hasDiagnosis) {
                                const detailIdx = hasDiagnosis ? idx - 1 : idx
                                onSwitchToAssessment(assessmentDetails[detailIdx])
                            }
                        }}
                    />
                ) : null
            })()}

            {/* Assessment Charts (Reused with internal fetching) */}
            <div style={{ marginBottom: 24 }}>
                <AssessmentCharts
                    assessment={mockAssessment}
                    instituteId={instituteId || instituteId} // ensure valid id
                    passingYear={passingYear}
                    isDiagnosis={true}
                    disableLineChart={true}
                    chartFilters={chartFiltersFromRedux}
                    setChartFilters={handleSetChartFilters}
                    clearChartFilters={handleClearChartFilters}
                    externalChartData={null}
                    // Pass empty arrays for student buckets as we don't need flow
                    studentDataCompleted={[]}
                    studentDataSent={[]}
                    studentDataPending={[]}
                    studentDataInProgress={[]}
                    studentDataDroppedOff={[]}
                />
            </div>

            <HeaderFullWidth>
                {/* Filters - Inline style matching CandidateList */}
                <FilterSection>
                    <FilterInlineContainer>
                        <FilterGrid>


                            <FilterGroup>
                                <FilterLabel>Degree</FilterLabel>
                                <StyledFormItem>
                                    <TreeSelect
                                        treeData={degreeOptions}
                                        value={topLevelDegree}
                                        onChange={setTopLevelDegree}
                                        treeCheckable={true}
                                        placeholder="Select Degree"
                                        labelInValue
                                        maxTagCount={0}
                                        maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                                        treeDefaultExpandAll={true}
                                    // disabled={topLevelDomain?.length === 0 && domainOptions?.length > 0}
                                    />
                                </StyledFormItem>
                            </FilterGroup>

                            <FilterGroup>
                                <FilterLabel>Department</FilterLabel>
                                <StyledFormItem>
                                    <TreeSelect
                                        treeData={departmentOptions}
                                        value={topLevelDepartment}
                                        onChange={setTopLevelDepartment}
                                        treeCheckable={true}
                                        placeholder="Select Department"
                                        labelInValue
                                        maxTagCount={0}
                                        maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                                        treeDefaultExpandAll={true}
                                        disabled={topLevelDegree?.length === 0 && degreeOptions?.length > 0}
                                    />
                                </StyledFormItem>
                            </FilterGroup>

                            <FilterGroup>
                                <FilterLabel>Specialization</FilterLabel>
                                <StyledFormItem>
                                    <TreeSelect
                                        treeData={specializationOptions}
                                        value={topLevelSpecialization}
                                        onChange={setTopLevelSpecialization}
                                        treeCheckable={true}
                                        placeholder="Select Spec."
                                        labelInValue
                                        maxTagCount={0}
                                        maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                                        treeDefaultExpandAll={true}
                                        disabled={topLevelDepartment?.length === 0 && departmentOptions?.length > 0}
                                    />
                                </StyledFormItem>
                            </FilterGroup>
                        </FilterGrid>
                    </FilterInlineContainer>
                </FilterSection>

                {filterArr.length > 0 && (
                    <FilterChipComponent
                        data={filterArr}
                        handleRemoveFilter={handleRemoveFilterChip}
                        handleClick={handleChipModalToggle}
                        open={isChipModalVisible}
                        visibleChipCount={7}
                    />
                )}

                {/* Search and Count */}
                <AlignedTableTop>
                    <SearchSection>
                        <StyledSearchInput
                            placeholder="Search by Name or Email"
                            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
                            value={localSearchTerm}
                            onChange={handleSearchChange}
                        />
                    </SearchSection>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <ShowingText>
                            Showing {localDiagnosisData.students?.length} of {localDiagnosisData.totalCount || 0}
                        </ShowingText>
                        <DownloadButton
                            icon={<DownloadOutlined />}
                            onClick={handleExport}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#3249d7',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                height: '36px',
                                fontWeight: 500
                            }}
                        >
                            Export
                        </DownloadButton>
                    </div>
                </AlignedTableTop>

                {/* Table */}
                <StyledTableSqare
                    columns={columns}
                    dataSource={localDiagnosisData.students || []}
                    pagination={false}
                    rowKey={record => record.email || record.name}
                    loading={isLoading}
                    onChange={onSorting}
                    bordered
                    scroll={{ x: '1280px' }}
                    locale={{
                        emptyText: <NoData>No diagnosis data found</NoData>,
                    }}
                />

                {/* Pagination */}
                {localDiagnosisData.totalCount > pageSize && (
                    <Pagination
                        current={currentPage}
                        total={localDiagnosisData.totalCount}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                    />
                )}
            </HeaderFullWidth>

            {/* Student Report Modal */}
            <StudentReportModal
                visible={drawerVisible}
                onClose={() => {
                    setDrawerVisible(false)
                    setSelectedStudent(null)
                }}
                student={selectedStudent}
                allowProctoring={false}
                isDiagnosis={true}
            />
        </DiagnosisListWrapper>
    )
}

export default DiagnosisList
